import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import {
  brainApi,
  scopeOf,
  BLUEPRINT_TYPE_LABELS,
  BLUEPRINT_TYPE_ORDER,
  BLUEPRINT_TYPE_SINGULAR,
  type Blueprint,
  type BlueprintType,
} from '@/modules/brain/services/brain.api';
import { BrainScopeRail, type ScopeSelection, type ScopeWorkspace } from './BrainScopeRail';
import { CustomFieldsBrainPage } from './CustomFieldsBrainPage';

const SCOPE_QUERY = 'scope';
const WS_QUERY = 'ws';

function readScope(params: URLSearchParams): ScopeSelection | null {
  const ws = params.get(WS_QUERY);
  if (ws) return { kind: 'workspace', workspaceId: ws };
  const scope = params.get(SCOPE_QUERY);
  if (scope === 'org') return { kind: 'org' };
  return null;
}

function writeScope(params: URLSearchParams, scope: ScopeSelection): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(SCOPE_QUERY);
  next.delete(WS_QUERY);
  if (scope.kind === 'org') next.set(SCOPE_QUERY, 'org');
  else if (scope.kind === 'workspace') next.set(WS_QUERY, scope.workspaceId);
  return next;
}

function isBlueprintType(value: string): value is BlueprintType {
  return (BLUEPRINT_TYPE_ORDER as string[]).includes(value);
}

export function BlueprintTypePage() {
  const { type: rawType } = useParams<{ type: string }>();

  // 'custom-field' is a virtual blueprint slug — defer to the real impl.
  if (rawType === 'custom-field') return <CustomFieldsBrainPage />;
  if (!rawType || !isBlueprintType(rawType)) {
    return <Navigate to={`/brain/blueprints/${BLUEPRINT_TYPE_ORDER[0]}`} replace />;
  }

  return <BlueprintTypeView type={rawType} />;
}

function BlueprintTypeView({ type }: { type: BlueprintType }) {
  const { hasPermission, currentWorkspaceId, workspaces } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const canManageOrg = hasPermission('blueprint.org.manage');
  const canManageWorkspace = hasPermission('blueprint.workspace.manage');
  const canViewOrg = hasPermission('blueprint.org.view') || canManageOrg;
  const canViewWorkspace = hasPermission('blueprint.workspace.view') || canManageWorkspace;

  const accessibleWorkspaces: ScopeWorkspace[] = useMemo(() => {
    if (!canViewWorkspace) return [];
    return workspaces.map((ws) => ({
      _id: ws._id,
      name: ws.name,
      current: ws._id === currentWorkspaceId,
    }));
  }, [canViewWorkspace, workspaces, currentWorkspaceId]);

  const defaultScope: ScopeSelection = useMemo(() => {
    const current = accessibleWorkspaces.find((w) => w.current);
    if (current) return { kind: 'workspace', workspaceId: current._id };
    if (canViewOrg) return { kind: 'org' };
    if (accessibleWorkspaces[0]) return { kind: 'workspace', workspaceId: accessibleWorkspaces[0]._id };
    return { kind: 'org' };
  }, [accessibleWorkspaces, canViewOrg]);

  const scope = readScope(searchParams) ?? defaultScope;
  const setScope = (next: ScopeSelection) => setSearchParams(writeScope(searchParams, next));

  const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTarget, setEditorTarget] = useState<Blueprint | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await brainApi.listBlueprints({ type });
      setBlueprints(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load blueprints';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const visibleItems = useMemo(() => {
    return blueprints.filter((b) => {
      const s = scopeOf(b);
      if (scope.kind === 'org') return s === 'org';
      if (scope.kind === 'workspace') return b.workspaceId === scope.workspaceId;
      return false;
    });
  }, [blueprints, scope]);

  const canManageHere =
    scope.kind === 'org' ? canManageOrg : scope.kind === 'workspace' ? canManageWorkspace : false;

  const scopeLabel = (() => {
    if (scope.kind === 'org') return 'Organization';
    if (scope.kind === 'workspace') {
      return accessibleWorkspaces.find((w) => w._id === scope.workspaceId)?.name || 'Workspace';
    }
    return '';
  })();

  const typeLabel = BLUEPRINT_TYPE_LABELS[type];
  const typeSingular = BLUEPRINT_TYPE_SINGULAR[type];

  const handleSave = async (data: { name: string; description: string; payload: Record<string, unknown> }) => {
    try {
      if (editorTarget) {
        const { data: updated } = await brainApi.updateBlueprint(editorTarget._id, {
          name: data.name,
          description: data.description,
          payload: data.payload,
        });
        setBlueprints((prev) => prev.map((b) => (b._id === updated._id ? updated : b)));
      } else {
        const { data: created } = await brainApi.createBlueprint({
          name: data.name,
          type,
          description: data.description,
          payload: data.payload,
          scope: scope.kind === 'workspace' ? 'workspace' : 'org',
          workspaceId: scope.kind === 'workspace' ? scope.workspaceId : undefined,
        });
        setBlueprints((prev) => [...prev, created]);
      }
      setEditorOpen(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save blueprint';
      setError(message);
    }
  };

  const handleDelete = async (b: Blueprint) => {
    if (!confirm(`Delete "${b.name}"?`)) return;
    const backup = blueprints;
    setBlueprints((prev) => prev.filter((x) => x._id !== b._id));
    try {
      await brainApi.deleteBlueprint(b._id);
    } catch (err: unknown) {
      setBlueprints(backup);
      const message = err instanceof Error ? err.message : 'Failed to delete';
      setError(message);
    }
  };

  return (
    <div className="brain-page">
      <BrainScopeRail
        title={typeLabel}
        subtitle="Reusable templates"
        current={scope}
        showOrg={canViewOrg}
        workspaces={accessibleWorkspaces}
        onSelect={setScope}
      />

      <section className="brain-content">
        <header className="brain-content-header">
          <div>
            <h1>{scopeLabel}</h1>
            <p>
              {scope.kind === 'org'
                ? `${typeLabel} shared across your organization.`
                : `${typeLabel} scoped to this workspace.`}
            </p>
          </div>
          {canManageHere && (
            <button
              type="button"
              className="brain-primary-button"
              onClick={() => {
                setEditorTarget(null);
                setEditorOpen(true);
              }}
            >
              <Plus size={14} strokeWidth={1.7} />
              <span>New {typeSingular.toLowerCase()}</span>
            </button>
          )}
        </header>

        {error && (
          <div className="brain-error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>dismiss</button>
          </div>
        )}

        {loading ? (
          <div className="brain-loading">Loading…</div>
        ) : visibleItems.length === 0 ? (
          <div className="brain-empty">
            <p>No {typeLabel.toLowerCase()} in {scopeLabel} yet.</p>
            {canManageHere && (
              <button
                type="button"
                className="brain-primary-button"
                onClick={() => {
                  setEditorTarget(null);
                  setEditorOpen(true);
                }}
              >
                <Plus size={14} strokeWidth={1.7} />
                <span>Create the first one</span>
              </button>
            )}
          </div>
        ) : (
          <ul className="brain-flat-list">
            {visibleItems.map((b) => (
              <li key={b._id} className="brain-row brain-row-card">
                <div className="brain-row-main">
                  <div className="brain-row-title">{b.name}</div>
                  {b.description && <p className="brain-row-description">{b.description}</p>}
                </div>
                {canManageHere && (
                  <div className="brain-row-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setEditorTarget(b);
                        setEditorOpen(true);
                      }}
                    >
                      Edit
                    </button>
                    <button type="button" className="danger" onClick={() => handleDelete(b)}>
                      Delete
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {editorOpen && (
        <BlueprintEditor
          existing={editorTarget}
          typeLabel={typeSingular}
          scopeLabel={scopeLabel}
          onClose={() => setEditorOpen(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function BlueprintEditor({
  existing,
  typeLabel,
  scopeLabel,
  onClose,
  onSave,
}: {
  existing: Blueprint | null;
  typeLabel: string;
  scopeLabel: string;
  onClose: () => void;
  onSave: (data: { name: string; description: string; payload: Record<string, unknown> }) => void;
}) {
  const [name, setName] = useState(existing?.name || '');
  const [description, setDescription] = useState(existing?.description || '');
  const [payloadText, setPayloadText] = useState(
    existing ? JSON.stringify(existing.payload || {}, null, 2) : '{}',
  );
  const [parseError, setParseError] = useState<string | null>(null);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    let payload: Record<string, unknown> = {};
    try {
      payload = payloadText.trim() ? JSON.parse(payloadText) : {};
    } catch {
      setParseError('Payload must be valid JSON');
      return;
    }
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: description.trim(), payload });
  };

  return (
    <div className="brain-modal-backdrop">
      <form onSubmit={submit} className="brain-modal">
        <header>
          <h2>
            {existing ? `Edit ${typeLabel.toLowerCase()}` : `New ${typeLabel.toLowerCase()}`}
          </h2>
          <p>Scope: {scopeLabel}</p>
        </header>
        <label className="brain-field">
          <span>Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            placeholder={`e.g. ${typeLabel} name`}
          />
        </label>
        <label className="brain-field">
          <span>Description</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </label>
        <label className="brain-field">
          <span>Payload (JSON)</span>
          <textarea
            value={payloadText}
            onChange={(e) => {
              setPayloadText(e.target.value);
              setParseError(null);
            }}
            rows={6}
            className="brain-mono"
          />
          {parseError && <small className="error">{parseError}</small>}
        </label>
        <footer>
          <button type="button" className="brain-secondary-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="brain-primary-button">
            {existing ? 'Save changes' : `Create ${typeLabel.toLowerCase()}`}
          </button>
        </footer>
      </form>
    </div>
  );
}
